import React from 'react';
import Close from '@mui/icons-material/Close';
import Info from '@mui/icons-material/Info';
import Warning from '@mui/icons-material/Warning';
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  SvgIconTypeMap,
  Typography
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { useTheme } from '@mui/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { BaseModal, BaseModalProps } from './BaseModal';

export type AlertProps = {
  variant: 'error' | 'info' | 'warning' | 'success';
  /**
   * Each message will be displayed in an unordered list.
   * For modified behavior, pass children instead of this param
   */
  messages?: string[];
  hideBulletPoints?: boolean;
  hideCloseIcon?: boolean;
} & BaseModalProps;

export class Alert<T extends AlertProps> extends BaseModal<T> {
  CloseIcon: React.FC = () => {
    if (this.props.hideCloseIcon) {
      return null;
    }
    return (
      <IconButton
        style={{
          position: 'absolute',
          zIndex: 100,
          right: '8px',
          top: '8px'
        }}
        onClick={this.props.onClose}
      >
        <Close />
      </IconButton>
    );
  };
  Icon: React.FC<any> = () => {
    const theme = useTheme();
    let color: string;
    let Comp: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
      muiName: string;
    };
    if (this.props.variant === 'info') {
      Comp = Info;
      color = theme.palette.primary[600];
    } else if (this.props.variant === 'warning') {
      Comp = Warning;
      color = theme.palette.warning[600];
    } else if (this.props.variant === 'success') {
      Comp = CheckCircleIcon;
      color = theme.palette.success[600];
    } else {
      Comp = Warning;
      color = theme.palette.error[600];
    }
    return (
      <Comp
        style={{
          ...this.iconStyle,
          color
        }}
      />
    );
  };
  Header: React.FC<{}> = () => {
    // const Icon = this.Icon;
    // const theme = useTheme();
    return null;
  };
  Content: React.FC<{}> = ({ children }) => {
    const Icon = this.Icon;
    const theme = useTheme();
    return (
      <DialogContent
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          paddingBottom: '10px'
        }}
      >
        <Grid
          container
          width={'100%'}
          display="flex"
          flexDirection={'column'}
          alignItems={'center'}
          mt={'30px'}
        >
          <Icon />

          <Typography
            variant="h5"
            style={{
              color: theme.palette.text.primary,
              padding: '16px'
            }}
          >
            {this.props.title}
          </Typography>
        </Grid>
        {this.props?.messages?.length && this.props?.messages?.length > 1 ? (
          this.props?.hideBulletPoints ? (
            this.props?.messages?.map(m => (
              <>
                <Typography variant="body4">{m}</Typography>
                <br />
              </>
            ))
          ) : (
            <ul>
              {this.props?.messages?.map(m => (
                <li>
                  <Typography variant="body4">{m}</Typography>
                </li>
              ))}
            </ul>
          )
        ) : (
          <Typography variant="body4">
            {this.props?.messages?.[0] || children}
          </Typography>
        )}
      </DialogContent>
    );
  };
  Actions: React.FC<any> = () => {
    return (
      <DialogActions style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          color="primary"
          variant="contained"
          onClick={this.props.onClose}
        >
          OK
        </Button>
      </DialogActions>
    );
  };
}
